import { create } from 'zustand';
import { supabase } from '../supabase';
import { Profile } from '../types';

interface AuthState {
  session: any | null;
  userProfile: Profile | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  userProfile: null,
  loading: true,
  error: null,

  initialize: async () => {
    console.log('[AUTH] initialize() called');
    set({ loading: true });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      console.log('[AUTH] getSession result:', {
        hasSession: !!session,
        userId: session?.user?.id,
        email: session?.user?.email,
      });
      set({ session });

      if (session?.user) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        console.log('[AUTH] Profile fetch result:', {
          hasProfile: !!profile,
          profileError: profileError?.message || null,
          factory_id: (profile as any)?.factory_id,
          role: (profile as any)?.role,
          full_name: (profile as any)?.full_name,
          rawProfile: profile,
        });

        if (profileError) {
          console.error('[AUTH] ❌ Profile fetch ERROR:', profileError);
        }

        set({ userProfile: profile as Profile });
      } else {
        console.log('[AUTH] No active session found, user must log in.');
      }
    } catch (err: any) {
      console.error('[AUTH] ❌ Auth init error:', err);
    } finally {
      set({ loading: false });
      console.log('[AUTH] initialize() complete. loading=false');
    }

    supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log('[AUTH] onAuthStateChange event:', _event, {
        hasSession: !!session,
        userId: session?.user?.id,
      });
      set({ session });
      if (session?.user && !get().userProfile) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        console.log('[AUTH] onAuthStateChange profile fetch:', {
          hasProfile: !!profile,
          profileError: profileError?.message || null,
          factory_id: (profile as any)?.factory_id,
          role: (profile as any)?.role,
          rawProfile: profile,
        });
        set({ userProfile: profile as Profile });
      } else if (!session) {
        console.log('[AUTH] Session cleared, resetting profile.');
        set({ userProfile: null });
      }
    });
  },

  signIn: async (email, password) => {
    console.log('[AUTH] signIn() called with email:', email);
    set({ loading: true, error: null });
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    console.log('[AUTH] signIn result:', {
      success: !error,
      userId: data?.user?.id,
      email: data?.user?.email,
      error: error?.message || null,
    });
    if (error) {
      console.error('[AUTH] ❌ Sign in FAILED:', error.message);
      set({ error: error.message, loading: false });
      throw error;
    }
    set({ loading: false });
  },

  signOut: async () => {
    set({ loading: true });
    await supabase.auth.signOut();
    set({ session: null, userProfile: null, loading: false });
  },
}));